import { useTranslation } from "react-i18next";
import { ACCOMMODATION_FLAGS, ROOMING_PERSON_STATES, ROOM_STATES, type RoomState, type RoomingPersonState } from "../../lib/accommodation/types";
import type { StatusGuideGroup, StatusGuideItem } from "../common/status-guide";
import { FlagTag } from "./parts";
import "./accommodation.css";

/**
 * A person's rooming state (her `AST`, V5 vocabulary): No bed · Assigned ·
 * Checked in · Checked out — a dot and the label in the state colour
 * (`accommodation.css`); `title` carries the one-line meaning.
 */
export function PersonStateChip({ state, className = "" }: { state: RoomingPersonState; className?: string }) {
	const { t } = useTranslation("accommodation");
	return (
		<span className={`acc-st ${className}`} data-acc-state={state} title={t(`person.hint.${state}`)}>
			{t(`person.state.${state}`)}
		</span>
	);
}

/** The dot alone (chips, the Statuses guide). */
export function PersonStateDot({ state }: { state: RoomingPersonState }) {
	return <span aria-hidden className="acc-dot" data-acc-state={state} />;
}

/** Her legend swatch for a room state (`.l3leg i`). */
export function RoomStateSwatch({ state }: { state: RoomState }) {
	return <span aria-hidden className="acc-swatch" data-room-state={state} />;
}

/** Her floor-map legend line: a swatch and the label per room state. */
export function RoomLegend() {
	const { t } = useTranslation("accommodation");
	return (
		<ul className="flex flex-wrap gap-x-[0.875rem] gap-y-[0.375rem] text-[0.75rem] text-gms-ink-2" aria-label={t("guide.rooms")}>
			{ROOM_STATES.map((s) => (
				<li key={s} className="inline-flex items-center gap-[0.375rem]">
					<RoomStateSwatch state={s} />
					{t(`room.state.${s}`)}
				</li>
			))}
		</ul>
	);
}

/**
 * The Statuses guide groups (her `legendSet('rooms', 'Room and stay statuses')`),
 * built from the same state lists and markers the pages draw. `markers`
 * adds the per-row flags the host lists show.
 */
export function useRoomingGuide({
	people = true,
	rooms = true,
	markers = false,
	beds = false,
}: { people?: boolean; rooms?: boolean; markers?: boolean; beds?: boolean } = {}): {
	title: string;
	groups: StatusGuideGroup[];
} {
	const { t } = useTranslation("accommodation");
	const groups: StatusGuideGroup[] = [];
	if (people)
		groups.push({
			key: "people",
			heading: t("guide.people"),
			items: ROOMING_PERSON_STATES.map((s): StatusGuideItem => ({
				key: s,
				label: t(`person.state.${s}`),
				description: t(`person.hint.${s}`),
				marker: <PersonStateDot state={s} />,
			})),
		});
	if (rooms)
		groups.push({
			key: "rooms",
			heading: t("guide.rooms"),
			items: ROOM_STATES.map((s): StatusGuideItem => ({
				key: s,
				label: t(`room.state.${s}`),
				description: t(`room.hint.${s}`),
				marker: <RoomStateSwatch state={s} />,
			})),
		});
	if (markers)
		groups.push({
			key: "markers",
			heading: t("guide.markers"),
			items: ACCOMMODATION_FLAGS.map((f): StatusGuideItem => ({
				key: f,
				label: t(`flag.label.${f}`),
				description: t(`flag.hint.${f}`),
				marker: <FlagTag flag={f} />,
			})),
		});
	if (beds)
		groups.push({
			key: "beds",
			heading: t("guide.beds"),
			items: [
				{ key: "free", label: t("guide.bed.free"), description: t("guide.bed.freeHint"), marker: <BedDot /> },
				{ key: "taken", label: t("guide.bed.taken"), description: t("guide.bed.takenHint"), marker: <BedDot on /> },
				{ key: "in", label: t("guide.bed.in"), description: t("guide.bed.inHint"), marker: <BedDot checkedIn /> },
			],
		});
	return { title: t("guide.title"), groups };
}

/** One bed slot dot as the floor map draws it (her `.l3room__d i`). */
function BedDot({ on = false, checkedIn = false }: { on?: boolean; checkedIn?: boolean }) {
	return (
		<span aria-hidden className="acc-beds mt-[0.1875rem]">
			<i data-on={on ? "" : undefined} data-in={checkedIn ? "" : undefined} />
		</span>
	);
}
